var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as AppService from "../service/app-service.js";
import { MenuOpenMode } from "./Enums.js";
import { Resource } from "./Resource.js";
import { Bkper } from "./Bkper.js";
/**
 * Defines an App on Bkper.
 *
 * Apps can be installed on Books by users.
 *
 * @public
 */
export class App extends Resource {
    constructor(payload, config) {
        super(payload);
        this.config = config;
    }
    /** @internal */
    getConfig() {
        return this.config || Bkper.globalConfig;
    }
    /**
     * Sets the webhook url for development.
     *
     * @param webhookUrlDev - The webhook URL for development
     *
     * @returns This App, for chaining
     */
    setWebhookUrlDev(webhookUrlDev) {
        this.payload.webhookUrlDev = webhookUrlDev;
        return this;
    }
    /**
     * Gets the App universal identifier.
     *
     * @returns The App universal identifier
     */
    getId() {
        return this.payload.id;
    }
    /**
     * Gets the name of this App.
     *
     * @returns The name of this App
     */
    getName() {
        return this.payload.name;
    }
    /**
     * Checks if this App has events bound to it.
     *
     * @returns True if this App has events bound to it
     */
    hasEvents() {
        const events = this.getEvents() || [];
        return events.length > 0;
    }
    /**
     * Gets the events bound to this App.
     *
     * @returns The events bound to this App
     */
    getEvents() {
        return this.payload.events;
    }
    /**
     * Checks if this App is published.
     *
     * @returns True if this App is published
     */
    isPublished() {
        return this.payload.published || false;
    }
    /**
     * Tells if this App is installable.
     *
     * @returns True if this App is installable
     */
    isInstallable() {
        return this.payload.installable || false;
    }
    /**
     * Gets the logo url of this App.
     *
     * @returns The logo url of this App
     */
    getLogoUrl() {
        return this.payload.logoUrl;
    }
    /**
     * Gets the logo url of this App in dark mode.
     *
     * @returns The logo url of this App in dark mode
     */
    getLogoUrlDark() {
        return this.payload.logoUrlDark;
    }
    /**
     * Gets the description of this App.
     *
     * @returns The description of this App
     */
    getDescription() {
        return this.payload.description;
    }
    /**
     * Sets the whitelisted users (usernames and domain patterns).
     *
     * @param users - The users to whitelist (comma or space separated usernames and domain patterns like *@domain.com)
     *
     * @returns This App for chaining
     */
    setUsers(users) {
        this.payload.users = users;
        return this;
    }
    /**
     * Gets the whitelisted users (usernames and domain patterns).
     *
     * @returns The users string
     */
    getUsers() {
        return this.payload.users;
    }
    /**
     * Gets the menu url of this App.
     *
     * @returns The menu url of this App
     */
    getMenuUrl() {
        return this.payload.menuUrl;
    }
    /**
     * Gets the menu development url of this App.
     *
     * @returns The menu development url of this App
     */
    getMenuUrlDev() {
        return this.payload.menuUrlDev;
    }
    /**
     * Gets the menu text of this App.
     *
     * @returns The menu text of this App
     */
    getMenuText() {
        return this.payload.menuText;
    }
    /**
     * Gets how the app menu opens.
     *
     * @returns The configured app menu open mode, defaults to SIDEBAR
     */
    getMenuOpenMode() {
        return this.payload.menuOpenMode || MenuOpenMode.SIDEBAR;
    }
    /**
     * Sets how the app menu opens.
     *
     * @param menuOpenMode - The app menu open mode to set
     *
     * @returns This App, for chaining
     */
    setMenuOpenMode(menuOpenMode) {
        this.payload.menuOpenMode = menuOpenMode;
        return this;
    }
    /**
     * Gets the menu popup width of this App.
     *
     * @deprecated Use getMenuOpenMode() to decide how the app should open.
     * @returns The menu popup width of this App
     */
    getMenuPopupWidth() {
        return this.payload.menuPopupWidth;
    }
    /**
     * Gets the menu popup height of this App.
     *
     * @deprecated Use getMenuOpenMode() to decide how the app should open.
     * @returns The menu popup height of this App
     */
    getMenuPopupHeight() {
        return this.payload.menuPopupHeight;
    }
    /**
     * Gets the name of the owner of this App.
     *
     * @returns The name of the owner of this App
     */
    getOwnerName() {
        return this.payload.ownerName;
    }
    /**
     * Gets the logo url of the owner of this App.
     *
     * @returns The logo url of the owner of this App
     */
    getOwnerLogoUrl() {
        return this.payload.ownerLogoUrl;
    }
    /**
     * Gets the website url of the owner of this App.
     *
     * @returns The website url of the owner of this App
     */
    getOwnerWebsiteUrl() {
        return this.payload.ownerWebsite;
    }
    /**
     * Gets the file patterns the App handles.
     *
     * @returns The file patterns the App handles - E.g *.pdf *.csv
     */
    getFilePatterns() {
        return this.payload.filePatterns;
    }
    /**
     * Sets the developers (usernames and domain patterns).
     *
     * @param developers - The developers (comma or space separated usernames and domain patterns like *@domain.com)
     *
     * @returns This App for chaining
     */
    setDevelopers(developers) {
        this.payload.developers = developers;
        return this;
    }
    /**
     * Gets the developers (usernames and domain patterns).
     *
     * @returns The developers string
     */
    getDevelopers() {
        return this.payload.developers;
    }
    /**
     * Sets the client secret.
     *
     * @param clientSecret - The client secret to set
     *
     * @returns This App for chaining
     */
    setClientSecret(clientSecret) {
        this.payload.clientSecret = clientSecret;
        return this;
    }
    /**
     * Gets the website url of this App.
     *
     * @returns The website url of this App
     */
    getWebsiteUrl() {
        return this.payload.website;
    }
    /**
     * Tells if the repository is private.
     *
     * @returns True if the repository is private
     */
    isRepositoryPrivate() {
        return this.payload.repoPrivate;
    }
    /**
     * Gets the repository url of this App.
     *
     * @returns The repository url of this App
     */
    getRepositoryUrl() {
        return this.payload.repoUrl;
    }
    /**
     * Gets the readme.md file as text.
     *
     * @returns The readme text
     */
    getReadme() {
        return this.payload.readmeMd || this.payload.readme;
    }
    /**
     * Sets the readme.md file as text.
     *
     * @param readme - The readme text to set
     *
     * @returns This App, for chaining
     */
    setReadme(readme) {
        this.payload.readmeMd = readme;
        return this;
    }
    /**
     * Performs the app creation, applying pending changes.
     *
     * The App id MUST be unique. If another app is already existing, an error will be thrown.
     *
     * @returns This App after creation
     */
    create() {
        return __awaiter(this, void 0, void 0, function* () {
            yield AppService.createApp(this.payload, this.getConfig());
            return this;
        });
    }
    /**
     * Performs a full update of the App, applying pending changes.
     *
     * @returns This App after the update
     */
    update() {
        return __awaiter(this, void 0, void 0, function* () {
            yield AppService.updateApp(this.payload, this.getConfig());
            return this;
        });
    }
}
//# sourceMappingURL=App.js.map